import { createApi } from '@reduxjs/toolkit/query/react';

import {
  AddUserCollegePayload,
  AwardLetter,
  CollegeComparison,
  ComparisonSettings,
  ComparisonSummary,
  CreateComparisonPayload,
  ScoreComparisonPayload,
  ScoredComparison,
  UpdateComparisonPayload,
  ExtractedAwardLetter,
  SaveAwardLetterPayload,
  UpdateUserCollegePayload,
  UserCollegesResponse,
} from './userColleges.types';
import { baseQueryWithAuth } from '../baseQuery';

export const userCollegesApi = createApi({
  reducerPath: 'userCollegesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['UserColleges', 'ComparisonList', 'Comparison', 'ComparisonDefaults'],
  endpoints: (build) => ({
    getUserColleges: build.query<UserCollegesResponse, undefined>({
      query: () => 'user-colleges',
      providesTags: [{ type: 'UserColleges' }],
    }),
    addUserCollege: build.mutation<unknown, AddUserCollegePayload>({
      query: (body) => ({ url: 'user-colleges', method: 'POST', body }),
      invalidatesTags: [{ type: 'UserColleges' }],
    }),
    updateUserCollege: build.mutation<unknown, UpdateUserCollegePayload>({
      query: ({ id, ...body }) => ({ url: `user-colleges/${id}`, method: 'PATCH', body }),
      async onQueryStarted({ id, ...changes }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          userCollegesApi.util.updateQueryData('getUserColleges', undefined, (draft) => {
            const college = draft.colleges.find((candidate) => candidate.id === id);

            if (college) Object.assign(college, changes);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: 'UserColleges' }],
    }),
    getAwardLetter: build.query<AwardLetter, number>({
      query: (userCollegeId) => `user-colleges/${userCollegeId}/award-letter`,
    }),
    saveAwardLetter: build.mutation<AwardLetter, SaveAwardLetterPayload>({
      query: ({ userCollegeId, media, ...body }) => ({
        url: `user-colleges/${userCollegeId}/award-letter`,
        method: 'PUT',
        body: { ...body, media_id: media?.id ?? null },
      }),
      async onQueryStarted({ userCollegeId, ...changes }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          userCollegesApi.util.updateQueryData('getAwardLetter', userCollegeId, (draft) => {
            Object.assign(draft, changes);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: 'UserColleges' }],
    }),
    extractAwardLetter: build.mutation<
      ExtractedAwardLetter,
      Readonly<{ userCollegeId: number; mediaId: number }>
    >({
      query: ({ userCollegeId, mediaId }) => ({
        url: `user-colleges/${userCollegeId}/award-letter/extract`,
        method: 'POST',
        body: { media_id: mediaId },
      }),
    }),
    removeUserCollege: build.mutation<unknown, number>({
      query: (id) => ({ url: `user-colleges/${id}`, method: 'DELETE' }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          userCollegesApi.util.updateQueryData('getUserColleges', undefined, (draft) => ({
            ...draft,
            colleges: draft.colleges.filter((college) => college.id !== id),
          })),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: 'UserColleges' }],
    }),
    getComparisons: build.query<readonly ComparisonSummary[], undefined>({
      query: () => 'user-college-comparisons',
      providesTags: [{ type: 'ComparisonList' }, { type: 'UserColleges' }],
    }),
    getComparisonDefaults: build.query<
      Readonly<{ settings: ComparisonSettings; saved: boolean }>,
      undefined
    >({
      query: () => 'user-college-comparisons/defaults',
      providesTags: [{ type: 'ComparisonDefaults' }],
    }),
    getComparison: build.query<CollegeComparison, number>({
      query: (id) => `user-college-comparisons/${id}`,
      providesTags: (_, __, id) => [{ type: 'Comparison', id }, { type: 'UserColleges' }],
    }),
    scoreComparison: build.query<ScoredComparison, ScoreComparisonPayload>({
      query: (body) => ({ url: 'user-college-comparisons/score', method: 'POST', body }),
      providesTags: [{ type: 'UserColleges' }, { type: 'ComparisonDefaults' }],
    }),
    createComparison: build.mutation<CollegeComparison, CreateComparisonPayload>({
      query: (body) => ({ url: 'user-college-comparisons', method: 'POST', body }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled.catch(() => ({ data: null }));

        if (!data) return;

        dispatch(userCollegesApi.util.upsertQueryData('getComparison', data.id, data));
        dispatch(
          userCollegesApi.util.updateQueryData('getComparisons', undefined, (draft) => [
            {
              id: data.id,
              name: data.name,
              institution_ids: data.colleges.map((college) => college.institution_id),
              updated_at: new Date().toISOString(),
            },
            ...draft,
          ]),
        );
      },
    }),
    updateComparison: build.mutation<CollegeComparison, UpdateComparisonPayload>({
      query: ({ id, ...body }) => ({
        url: `user-college-comparisons/${id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled.catch(() => ({ data: null }));

        if (data) dispatch(userCollegesApi.util.upsertQueryData('getComparison', id, data));
      },
      invalidatesTags: [{ type: 'ComparisonList' }],
    }),
    deleteComparison: build.mutation<unknown, number>({
      query: (id) => ({ url: `user-college-comparisons/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'ComparisonList' }],
    }),
  }),
});

export const {
  useGetUserCollegesQuery,
  useAddUserCollegeMutation,
  useUpdateUserCollegeMutation,
  useRemoveUserCollegeMutation,
  useGetAwardLetterQuery,
  useSaveAwardLetterMutation,
  useExtractAwardLetterMutation,
  useGetComparisonsQuery,
  useGetComparisonQuery,
  useGetComparisonDefaultsQuery,
  useScoreComparisonQuery,
  useCreateComparisonMutation,
  useUpdateComparisonMutation,
  useDeleteComparisonMutation,
} = userCollegesApi;
